import { FC, useRef, useEffect } from "react";
import { Flex, TextField } from "@radix-ui/themes";

interface VerificationCodeInputProps {
  code: {
    0: number | null;
    1: number | null;
    2: number | null;
    3: number | null;
    4: number | null;
    5: number | null;
  };
  setFieldValue: (index: number, value: number | null) => void;
}

export const VerificationCodeInput: FC<VerificationCodeInputProps> = ({
  code,
  setFieldValue,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus the first empty input on mount
  useEffect(() => {
    const firstEmptyIndex = Object.values(code).findIndex(
      (val) => val === null
    );
    if (firstEmptyIndex >= 0 && firstEmptyIndex < 6) {
      inputRefs.current[firstEmptyIndex]?.focus();
    } else if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInput = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    // Update the value
    setFieldValue(index, value ? parseInt(value) : null);

    // Auto-focus next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys for navigation
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste event to fill multiple fields at once
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .substring(0, 6);

    // Fill the inputs with the pasted data
    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6) {
        const value = parseInt(pastedData[i]);
        setFieldValue(i, value);
      }
    }

    // Focus the next empty field or the last field
    const nextEmptyIndex = Math.min(
      Object.values(code).findIndex((val) => val === null) || 5,
      5
    );
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  return (
    <Flex gap="2" justify="center">
      {/* Verify Email */}
      <Flex gap="2">
        {Array.from(Array(6), (_, index) => {
          const key = `auth-${index}`;
          return (
            <TextField.Root
              size="3"
              type="number"
              id={`authcode-${index}`}
              maxLength={1}
              value={code[index] !== null ? code[index]?.toString() : ""}
              key={key}
              onPaste={index === 0 ? handlePaste : undefined}
              onChange={(e) => handleInput(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};
